import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Music, Search, Upload, Loader2, Plus, ListMusic, Pencil, Trash2, Eye } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { usePermissions } from '@meno/ui/hooks/usePermissions'
import { TabButton } from '@meno/ui/components/shared/TabButton'
import { useUpload } from '@/contexts/UploadContext'
import {
  getAudios, updateAudio, archiveAudio,
  getAudioPlaylists, createAudioPlaylist, deleteAudioPlaylist,
  publishAudio, publishAudioPlaylist,
} from '@meno/ui/lib/api'
import { AudioCard, AudioPlaylistCard } from './management/media-page/cards'
import { RenameDialog } from './management/media-page/dialogs'
import { type AudioFile, type AudioPlaylist, type MediaFile } from './management/media-page/utils'
import AssetPreviewDialog from '@/components/AssetPreviewDialog'
import { PublishDialog } from '@/components/PublishDialog'
import EmptyState from '@/components/EmptyState'
import ViewToggle, { type ViewMode } from '@/components/ViewToggle'
import { formatDuration } from '@meno/ui/lib/utils'

// Audio Hub — a standalone library of background-audio tracks plus audio-only
// playlists. Two tabs: "Tracks" (the track library — upload, rename, audition,
// archive, and now **stream** a single track to screens) and "Playlists"
// (audio-only playlists that can be streamed as a sequence). Streaming an
// audio source overrides whatever audio is playing on the target screens; it's
// a parallel channel that leaves visual content untouched.
export default function AudioHubPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canUpload = can('media:upload')       // audio files are media assets
  const canCreatePlaylist = can('playlists:create')
  const canStream = can('playlists:edit')     // streaming to screens
  usePageHeader({ title: t('nav.audioHub', 'Audio') })
  const { openUpload } = useUpload()

  const [tab, setTab] = useState<'tracks' | 'playlists'>('tracks')
  const [view, setView] = useState<ViewMode>('grid')

  const [audios, setAudios] = useState<AudioFile[]>([])
  const [playlists, setPlaylists] = useState<AudioPlaylist[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [search, setSearch] = useState('')

  const [renameTarget, setRenameTarget] = useState<AudioFile | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AudioFile | null>(null)
  const [previewTarget, setPreviewTarget] = useState<AudioFile | null>(null)
  const [deletePlaylist, setDeletePlaylist] = useState<AudioPlaylist | null>(null)
  // The audio source to stream — drives the shared PublishDialog.
  const [streamTarget, setStreamTarget] =
    useState<{ kind: 'audio' | 'playlist'; id: number } | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([getAudios(), getAudioPlaylists()])
      .then(([a, p]) => { setAudios(a.data); setPlaylists(p.data) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  // Upload runs through the app-wide universal upload modal (audio mode) — the
  // same Assets-page experience, batch-aware, with the client-side duration
  // probe handled by the provider. New tracks are prepended as they land.
  const openAudioUpload = () => openUpload({
    accept: 'audio/*',
    allowAudio: true,
    onUploaded: (asset) => { if (asset.kind === 'audio') setAudios(prev => [asset.audio, ...prev]) },
  })

  const handleRename = async (name: string) => {
    if (!renameTarget) return
    try {
      await updateAudio(renameTarget.id, { original_name: name })
      setAudios(prev => prev.map(a => a.id === renameTarget.id ? { ...a, original_name: name } : a))
      toast.success(t('common.renamedSuccess', 'Renamed successfully'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setRenameTarget(null)
    }
  }

  // Delete = archive (soft delete), consistent with the Assets page. The
  // track can still be restored from Assets → Archive.
  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await archiveAudio(deleteTarget.id)
      setAudios(prev => prev.filter(a => a.id !== deleteTarget.id))
      toast.success(t('assets.movedToArchive', 'Moved to Archive'))
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'))
    } finally {
      setDeleteTarget(null)
    }
  }

  const handleCreatePlaylist = async () => {
    setCreating(true)
    try {
      const { data } = await createAudioPlaylist({ name: t('audioHub.newPlaylistName', 'New audio playlist') })
      navigate(`/audio-hub/playlists/${data.id}`)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'))
    } finally {
      setCreating(false)
    }
  }

  const confirmDeletePlaylist = async () => {
    if (!deletePlaylist) return
    try {
      await deleteAudioPlaylist(deletePlaylist.id)
      setPlaylists(prev => prev.filter(p => p.id !== deletePlaylist.id))
      toast.success(t('common.deleted', 'Deleted'))
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'))
    } finally {
      setDeletePlaylist(null)
    }
  }

  const filtered = audios.filter(a =>
    !search.trim() || a.original_name.toLowerCase().includes(search.toLowerCase()),
  )
  const filteredPlaylists = playlists.filter(p =>
    !search.trim() || p.name.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    // Bounded to the shell panel so the empty state fills the leftover height
    // (flex-1) instead of reading as a short card near the top; the grid
    // scrolls in its own region while the tabs/toolbar stay pinned.
    <div className="flex flex-col gap-6 flex-1 min-h-0">
      {/* Tab strip — flush under the TopBar, mirrors the Content Hub. */}
      <div className="-mx-4 sm:-mx-[var(--space-l)] -mt-1 sm:-mt-[calc(var(--space-l)-0.75rem)] mb-0 px-4 sm:px-[var(--space-l)] border-b border-border bg-transparent shrink-0">
        <div className="flex gap-1">
          <TabButton active={tab === 'tracks'}    onClick={() => setTab('tracks')}    icon={Music}    label={t('audioHub.tracks', 'Tracks')} />
          <TabButton active={tab === 'playlists'} onClick={() => setTab('playlists')} icon={ListMusic} label={t('audioHub.playlists', 'Playlists')} />
        </div>
      </div>

      {/* Search + primary action row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 shrink-0">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={tab === 'tracks'
              ? t('audioHub.search', 'Search Audio…')
              : t('audioHub.searchPlaylists', 'Search audio playlists…')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="ps-9 h-9 transition-colors focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:text-foreground"
          />
        </div>
        <ViewToggle mode={view} onChange={setView} />
        {tab === 'tracks' ? (
          canUpload && (
            <Button
              size="sm"
              className="gap-2 h-9 shrink-0"
              onClick={openAudioUpload}
            >
              <Upload className="w-4 h-4" />
              {t('audioHub.upload', 'Upload Audio')}
            </Button>
          )
        ) : (
          canCreatePlaylist && (
            <Button
              size="sm"
              className="gap-2 h-9 shrink-0"
              disabled={creating}
              onClick={handleCreatePlaylist}
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t('audioHub.newPlaylist', 'New Playlist')}
            </Button>
          )
        )}
      </div>

      {/* Body — fills remaining height; grid scrolls, empty state centers. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="aspect-video w-full rounded-xl" />
          ))}
        </div>
      ) : tab === 'tracks' ? (
        filtered.length === 0 ? (
          <EmptyState
            icon={Music}
            title={t('audioHub.emptyTitle', 'No audio yet')}
            description={t('audioHub.emptyDescription', 'Upload audio tracks to build your background-music library. Tracks here can play behind your content on screens.')}
          />
        ) : view === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filtered.map(audio => (
              <AudioCard
                key={audio.id}
                file={audio}
                onDelete={() => setDeleteTarget(audio)}
                onRename={() => setRenameTarget(audio)}
                onPreview={() => setPreviewTarget(audio)}
                onPublish={canStream ? () => setStreamTarget({ kind: 'audio', id: audio.id }) : undefined}
              />
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {filtered.map(audio => (
              <AudioTrackListRow
                key={audio.id}
                file={audio}
                onDelete={() => setDeleteTarget(audio)}
                onRename={() => setRenameTarget(audio)}
                onPreview={() => setPreviewTarget(audio)}
                onPublish={canStream ? () => setStreamTarget({ kind: 'audio', id: audio.id }) : undefined}
              />
            ))}
          </ul>
        )
      ) : (
        filteredPlaylists.length === 0 ? (
          <EmptyState
            icon={ListMusic}
            title={t('audioHub.emptyPlaylistsTitle', 'No audio playlists yet')}
            description={t('audioHub.emptyPlaylistsDescription', 'Create an audio playlist to stream a sequence of tracks to your screens.')}
          />
        ) : view === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredPlaylists.map(pl => (
              <AudioPlaylistCard
                key={pl.id}
                playlist={pl}
                onOpen={() => navigate(`/audio-hub/playlists/${pl.id}`)}
                onStream={canStream ? () => setStreamTarget({ kind: 'playlist', id: pl.id }) : undefined}
                onDelete={() => setDeletePlaylist(pl)}
              />
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {filteredPlaylists.map(pl => (
              <AudioPlaylistListRow
                key={pl.id}
                playlist={pl}
                onOpen={() => navigate(`/audio-hub/playlists/${pl.id}`)}
                onStream={canStream ? () => setStreamTarget({ kind: 'playlist', id: pl.id }) : undefined}
                onDelete={() => setDeletePlaylist(pl)}
              />
            ))}
          </ul>
        )
      )}
      </div>

      {/* Dialogs */}
      {renameTarget && (
        <RenameDialog
          file={renameTarget as unknown as MediaFile}
          onSave={handleRename}
          onClose={() => setRenameTarget(null)}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('audioHub.deleteTitle', 'Delete Audio')}
        description={t('audioHub.deleteDesc', 'This track will be moved to the Archive. You can restore it from Assets → Archive.')}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={!!deletePlaylist}
        title={t('audioHub.deletePlaylistTitle', 'Delete audio playlist')}
        description={t('audioHub.deletePlaylistDesc', 'This audio playlist will be deleted. The individual tracks stay in your library.')}
        onConfirm={confirmDeletePlaylist}
        onClose={() => setDeletePlaylist(null)}
      />

      {previewTarget && (
        <AssetPreviewDialog
          asset={{ kind: 'audio', file: previewTarget }}
          onClose={() => setPreviewTarget(null)}
        />
      )}

      <PublishDialog
        open={!!streamTarget}
        kind="audio"
        publish={(payload) =>
          streamTarget?.kind === 'playlist'
            ? publishAudioPlaylist(streamTarget.id, payload)
            : publishAudio(streamTarget!.id, payload)
        }
        onClose={() => setStreamTarget(null)}
        onPublished={() => setStreamTarget(null)}
      />
    </div>
  )
}

// ── List rows ──────────────────────────────────────────────────────────────
// Compact one-line variants for the list view — icon + name + meta on the
// start, inline actions on the end (no dropdown, so the row stays terse).
function AudioTrackListRow({
  file, onDelete, onRename, onPreview, onPublish,
}: {
  file: AudioFile
  onDelete: () => void
  onRename: () => void
  onPreview: () => void
  onPublish?: () => void
}) {
  const { t } = useTranslation()
  return (
    <li className="group flex items-center gap-3 rounded-xl bg-card px-3 py-2 hover:bg-muted/40 transition-colors">
      <div className="w-9 h-9 rounded-lg bg-emerald-500/10 shrink-0 flex items-center justify-center">
        <Music className="w-4 h-4 text-emerald-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{file.original_name}</p>
        {file.duration ? (
          <p className="text-[11px] text-muted-foreground">{formatDuration(file.duration)}</p>
        ) : null}
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <IconAction icon={Eye}    label={t('common.preview', 'Preview')} onClick={onPreview} />
        {onPublish && <IconAction icon={Upload} label={t('audioHub.stream', 'Stream')} onClick={onPublish} />}
        <IconAction icon={Pencil} label={t('common.rename', 'Rename')}    onClick={onRename} />
        <IconAction icon={Trash2} label={t('common.delete', 'Delete')}    onClick={onDelete} danger />
      </div>
    </li>
  )
}

function AudioPlaylistListRow({
  playlist, onOpen, onStream, onDelete,
}: {
  playlist: AudioPlaylist
  onOpen: () => void
  onStream?: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const count = playlist.track_count ?? playlist.tracks?.length ?? 0
  return (
    <li
      role="button" tabIndex={0} onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onOpen() }}
      className="group flex items-center gap-3 rounded-xl bg-card px-3 py-2 hover:bg-muted/40 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30"
    >
      <div className="w-9 h-9 rounded-lg bg-pink-500/10 shrink-0 flex items-center justify-center">
        <ListMusic className="w-4 h-4 text-pink-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{playlist.name}</p>
        <p className="text-[11px] text-muted-foreground">
          {t('audioHub.trackCount', { defaultValue: '{{count}} track', count })}
        </p>
      </div>
      <div className="flex items-center gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
        {onStream && <IconAction icon={Upload} label={t('audioHub.stream', 'Stream')} onClick={onStream} />}
        <IconAction icon={Trash2} label={t('common.delete', 'Delete')}   onClick={onDelete} danger />
      </div>
    </li>
  )
}

function IconAction({
  icon: Icon, label, onClick, danger,
}: {
  icon: typeof Music
  label: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); onClick() }}
      aria-label={label}
      title={label}
      className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
        danger
          ? 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      }`}
    >
      <Icon className="w-4 h-4" />
    </button>
  )
}


